<script>
    // import for modal window
    import AloneAction from './AloneAction.svelte';
    import Action_Park from './Action_Park.svelte';
    import Action_TV from './Action_TV.svelte';
    import Action_Shop from './Action_Shop.svelte';

    import {
        health, energy, hygiene, culture,
        iq, loneliness, confidence, stress,
        alcoholism, drugs, smooth,
        honesty, optimism, fitness,
        money, time, date,
        home_cleanliness,
        TimeEnum, TimeToPrint,
        upcomingEvent, Action,
        energy_geq_90, iq_geq_99,
        modal,
        daysElapsed,
        times_stared_at_walls, have_tv, addUpcomingEvent, getDateAfterDays, rnd
    } from './stores.js';
    import { applyEffects, describeEffects } from './effects.js';

    // Each *Effects array is the single source of truth for what an action
    // does; describeEffects() below derives its badges from it, so the two
    // can never drift out of sync the way hand-written badges used to.

    $: aaEffects = [
        { stat: energy, range: [-0.05, -0.04], badge: false },
        { stat: loneliness, range: [-0.3, -0.2] },
        { stat: optimism, range: [0.01, 0.05] },
        { stat: alcoholism, range: [-0.1, -0.02] },
    ];
    $: beerEffects = [
        { stat: energy, range: [-0.05, -0.03], badge: false },
        { stat: loneliness, range: [0.06, 0.12] },
        { stat: optimism, range: [0.01, 0.05] },
        { stat: confidence, range: [0.05, 0.1] },
        { stat: alcoholism, range: [0.05, 0.07], badge: false },
    ];
    $: burbonEffects = [
        { stat: energy, range: [-0.04, -0.02], badge: false },
        { stat: loneliness, range: [0.05, 0.1] },
        { stat: optimism, range: [0.01, 0.15] },
        { stat: confidence, range: [0.08, 0.12] },
        { stat: alcoholism, range: [0.05, 0.07], badge: false },
        { stat: smooth, range: [0.05, 0.08] },
    ];
    $: joggingEffects = [
        { stat: energy, compute: () => -rnd(0.15, 0.2) * (1.5 - $fitness), sign: '-', badge: false },
        { stat: loneliness, range: [0.04, 0.06], badge: false },
        { stat: smooth, range: [-0.05, -0.03], badge: false },
        { stat: fitness, range: [0.05, 0.09] },
        // sign flips with current fitness (below/above 0.5) — no honest static badge
        { stat: optimism, compute: () => rnd(0.07, 0.09) * (2. * $fitness - 1.), badge: false },
        { stat: health, range: [0.03, 0.05] },
        { stat: hygiene, value: -0.15 },
        { stat: home_cleanliness, value: -0.05, badge: false },
    ];
    $: swimEffects = [
        { stat: energy, compute: () => -rnd(0.27, 0.29) * (1.4 - $fitness), sign: '-', badge: false },
        { stat: loneliness, range: [0.02, 0.04], badge: false },
        { stat: fitness, range: [0.09, 0.15] },
        { stat: optimism, compute: () => rnd(0.05, 0.07) * (2. * $fitness - 1.), badge: false },
        { stat: health, range: [0.04, 0.08] },
    ];
    $: shoppingEffects = [
        { stat: stress, range: [-0.12, -0.09] },
        { stat: energy, range: [-0.12, -0.09], badge: false },
        { stat: optimism, range: [0.0, 0.1], badge: false },
        { stat: loneliness, range: [0.05, 0.09], badge: false },
        { stat: smooth, range: [0.04, 0.05], badge: false },
        { stat: home_cleanliness, value: -0.05, badge: false },
    ];
    $: takeawayEffects = [
        { stat: fitness, range: [-0.09, -0.07], badge: false },
        { stat: health, range: [-0.08, -0.05] },
        { stat: optimism, range: [0.02, 0.12] },
        { stat: loneliness, range: [0.05, 0.12] },
        { stat: smooth, range: [-0.06, -0.04] },
        { stat: home_cleanliness, value: -0.1, badge: false },
    ];
    $: bathEffects = [
        { stat: loneliness, range: [0.15, 0.23] },
        { stat: hygiene, range: [0.35, 0.39] },
        { stat: energy, range: [0.24, 0.28] },
        { stat: stress, range: [-0.22, -0.02] },
        { stat: home_cleanliness, value: -0.05, badge: false },
    ];
    $: napEffects = [
        { stat: loneliness, range: [0.1, 0.15] },
        { stat: energy, range: [0.3, 0.5] },
        { stat: optimism, range: [-0.1, -0.04], badge: false },
        { stat: stress, range: [-0.03, -0.02] },
        { stat: alcoholism, range: [-0.35, -0.15] },
        { stat: home_cleanliness, value: -0.08, badge: false },
    ];
    $: stareAtWallsEffects = [
        { stat: loneliness, range: [0.15, 0.45] },
        { stat: fitness, value: -0.02, badge: false },
        { stat: smooth, range: [-0.06, -0.04], badge: false },
        { stat: energy, range: [0.15, 0.23] },
        { stat: optimism, range: [-0.19, -0.17], badge: false },
        { stat: stress, range: [-0.22, -0.15] },
        { stat: alcoholism, range: [-0.06, -0.05], badge: false },
        { stat: home_cleanliness, value: -0.05, badge: false },
    ];
    $: houseCleaningEffects = [
        { stat: loneliness, range: [0.21, 0.23] },
        { stat: smooth, range: [-0.06, -0.04], badge: false },
        { stat: energy, range: [-0.3, -0.2] },
        { stat: optimism, range: [-0.09, -0.07], badge: false },
        { stat: home_cleanliness, value: 0.7, label: 'clean home' },
    ];
    $: churchEffects = [
        { stat: energy, range: [-0.07, -0.02], badge: false },
        { stat: loneliness, range: [-0.1, -0.05] },
        { stat: optimism, range: [0.03, 0.12], badge: false },
        { stat: confidence, range: [0.03, 0.06], badge: false },
        { stat: stress, range: [-0.08, -0.01], badge: false },
        { stat: honesty, range: [0.05, 0.6] },
    ];
    $: meditateEffects = [
        { stat: optimism, range: [0.03, 0.05], badge: false },
        { stat: stress, range: [-0.4, -0.2] },
        { stat: alcoholism, range: [-0.1, -0.05], badge: false },
        { stat: health, range: [0.08, 0.12], badge: false },
    ];
    $: walkInParkEffects = [
        { stat: fitness, range: [0.02, 0.05], badge: false },
        { stat: energy, range: [-0.06, -0.04], badge: false },
        { stat: stress, range: [-0.05, -0.03] },
        { stat: optimism, range: [0.05, 0.1] },
    ];
    $: pushupsEffects = [
        // the weaker you are the more benefit it brings; always a positive delta
        { stat: fitness, compute: () => rnd(0.08, 0.1) * (1.5 - $fitness), sign: '+' },
        { stat: energy, range: [-0.15, -0.06], badge: false },
        { stat: stress, value: 0.01 },
    ];
    $: practiceBassEffects = [
        // TODO: + bass guitar skill
        { stat: stress, range: [-0.15, -0.1] },
        { stat: culture, range: [0.01, 0.02] },
        { stat: smooth, range: [0.01, 0.02] },
        { stat: energy, range: [-0.25, -0.1], badge: false },
    ];

    $: aloneActions = [
        new Action({
            name: "Alcoholics Anonymous",
            description: "is a self-supporting group. Stay sober and help other alcoholics achieve sobriety.",
            ...describeEffects(aaEffects),
            effect: () => applyEffects(aaEffects),
            visible: ($alcoholism > 0.92) && ($date.getDay() === 2),
            // TODO: remove alcoholism flag after a few sessions. Add flag for visibility!
        }),
        new Action({
            name: "Have a beer",
            description: "Relax maaan. Sit down and have a cold one.",
            ...describeEffects(beerEffects),
            price: 1.5,
            effect: () => applyEffects(beerEffects),
        }),
        new Action({
            name: "Enjoy a glass of burbon",
            description: "Sip on that fine burbon you bought. Hard work is paying off.",
            ...describeEffects(burbonEffects),
            visible: false, // TODO: check inventory for the bottle
            effect: () => applyEffects(burbonEffects),
        }),
        new Action({
            name: "Go jogging",
            description: "Stay fit by jogging around the local park. The cheap way to get out and do something meaningful.",
            ...describeEffects(joggingEffects),
            possible: $energy >= 0.4,
            effect: () => applyEffects(joggingEffects),
        }),
        new Action({
            name: "Swim",
            description: "Perfect fusion of aerobic exercise and checking out the bikini hotties.",
            ...describeEffects(swimEffects),
            price: 2.5,
            possible: $energy >= 0.2,
            visible: $daysElapsed > 52,
            effect: () => applyEffects(swimEffects),
        }),
        new Action({
            name: "Go Shopping",
            description: "All the hard earned money needs to be spent somewhere.",
            ...describeEffects(shoppingEffects),
            price: 1.,
            possible: $energy >= 0.09,
            visible: [TimeEnum.morning, TimeEnum.evening].includes($time),
            effect: () => {
                applyEffects(shoppingEffects);
                modal.set(Action_Shop)
            },
        }),
        new Action({
            name: "Grab a takeaway meal",
            description: "Treat yourself with greasy happiness in a takeout box.",
            ...describeEffects(takeawayEffects),
            price: 6.,
            effect: () => applyEffects(takeawayEffects),
        }),
        new Action({
            name: "Have a bath!",
            description: "Cleanliness is next to godliness. But more importantly relax like a royalty after a hard day's work.",
            ...describeEffects(bathEffects),
            price: 1.5,
            effect: () => applyEffects(bathEffects),
        }),
        new Action({
            name: "Nap on sofa",
            description: "Crash in your sofa and take a nap. A tried-and-true hangover cure.",
            ...describeEffects(napEffects),
            effect: () => applyEffects(napEffects),
        }),
        new Action({
            name: "Stare at walls",
            description: "Sit around the house, not doing anything in particular.",
            ...describeEffects(stareAtWallsEffects),
            effect: () => {
                applyEffects(stareAtWallsEffects);
                $times_stared_at_walls += 1;
            },
        }),
        // new Action({
        //     name: "Watch TV",
        //     description: "Watch broadcasts of news, art, and cooking shows.",
        //     green_badges: ['~ depends on what you watch',],
        //     possible: $have_tv,
        //     effect: () => {
        //         $home_cleanliness -= 0.05;
        //         $optimism -= rnd(0.01, 0.1);
        //         $fitness -= 0.03;
        //         modal.set(Action_TV)
        //     },
        // }),
        new Action({
            name: "House cleaning chores",
            description: "Dirty house can be hazardous for your health.",
            ...describeEffects(houseCleaningEffects),
            visible: [TimeEnum.morning, TimeEnum.evening].includes($time),
            possible: $energy > 0.25,
            effect: () => applyEffects(houseCleaningEffects),
        }),
        // new Action({
        //     name: "Charity run",
        //     description: "Municipality organized run for a good cause. Contribute by getting out of breath and sweating.",
        //     green_badges: ['+ optimism', '+ fitness', '- loneliness'],
        //     red_badges: ['- energy'],
        //     visible: [TimeEnum.evening].includes($time) && ($date.getDay() === 0),  // Sunday
        //     possible: ($energy > 0.5) && ($fitness > 0.4),
        //     // TODO: this should be a scheduled event
        //     effect: () => {
        //         $fitness += 0.2;
        //         $energy -= rnd(0.3, 0.6);
        //         $smooth += rnd(0.05, 0.15);
        //         $loneliness -= rnd(0.21, 0.5);
        //         $optimism += rnd(0.2, 0.5);
        //     },
        // }),
        new Action({
            name: "Visit Church",
            description: "Be pious in the times of sex, drugs, and violence.",
            ...describeEffects(churchEffects),
            visible: [TimeEnum.morning, TimeEnum.evening].includes($time) &&
                ([0, 6].includes($date.getDay())) &&
                $daysElapsed > 12,
            effect: () => applyEffects(churchEffects),
        }),
        new Action({
            name: "Meditate",
            description: "Empty your head of thoughts. Be present, and fart occasionally.",
            ...describeEffects(meditateEffects),
            visible: $times_stared_at_walls >= 10,  // TODO: or learned from a book or a social encounter
            effect: () => applyEffects(meditateEffects),
        }),
        new Action({
            name: "Walk in Park",
            description: "Take stroll in the nearby park.",
            ...describeEffects(walkInParkEffects),
            visible: $daysElapsed >= 5,
            effect: () => applyEffects(walkInParkEffects),
        }),
        new Action({
            name: "Do some pushups",
            description: "Get some G.I. Joe vibes going. A cheap way to get fit at home.",
            ...describeEffects(pushupsEffects),
            visible: $daysElapsed >= 7,
            possible: $energy > 0.3,
            effect: () => applyEffects(pushupsEffects),
        }),

        // ITEMS RELATED ACTIONS
        // TODO: rubics cube
        // TODO: roller skating
        // TODO: photo shooting


        // MUSIC RELATED ACTIONS
        new Action({
            name: "Practice Bass",
            description: "Slap that bass and learn how to groove.",
            ...describeEffects(practiceBassEffects),
            visible: $energy > 0.2,  // TODO: and owns a bass guitar
            effect: () => applyEffects(practiceBassEffects),
        }),
        // .. guitar, keys, drums, sax




        // BOOK READING ACTIONS

        // DEBUG ACTIONS

        // new Action({
        //     name: "Nap on sofa",
        //     description: "Crash in your sofa and take a nap. A tried-and-true hangover cure.",
        //     green_badges: ['+ energy', '- stress'],
        //     red_badges: ['- hygiene'],
        //     effect: () => {$energy += 0.4; $stress -= 0.2; $hygiene -= 0.14},
        // }),
        // new Action({
        //     name: "add IQ",
        //     description: "add iq.",
        //     green_badges: ['+ iq'],
        //     effect: () => {$iq += 0.2},
        // }),
        // new Action({
        //     name: "Stroll in the park",
        //     description: "Relax by taking a leasurely walk in a nearby park.",
        //     green_badges: ['- stress'],
        //     effect: () => { modal.set(Action_Park) },
        // }),
        // new Action({
        //     name: "Disabled action",
        //     description: "This action should be disabled if energy is below 0.9.",
        //     green_badges: ['...'],
        //     red_badges: ['...'],
        //     possible: $energy_geq_90,
        //     effect: () => { console.log('Disabled action test...') },
        // }),
        // new Action({
        //     name: "Invisible action",
        //     description: "This action should only be visible with max IQ.",
        //     green_badges: ['invent stuff'],
        //     red_badges: ['+ stress'],
        //     visible: $iq_geq_99,
        //     effect: () => { console.log('Trying to invent stuff...') },
        // }),
    ]

</script>

<div>
    {#each aloneActions as a}
        <AloneAction action = {a} />
    {/each}
    <button on:click="{() => addUpcomingEvent({
            name: 'Dentist',
            date: getDateAfterDays(rnd(1, 1)),
            time: TimeEnum.morning,
    })}">
        Add dentist in 2 days
    </button>

</div>

<style>
    div {
        padding: 0em 0.3em 0 0.4em;
    }
</style>